/**
 * TOTP (RFC 6238) 実装 (T-D-Vault-TOTP)。
 *
 * 目的: VaultDetailPage の login type で `totpSecret` (Base32) を保存している
 * アイテムに対し、6 桁の rotating one-time password を 30 秒周期で表示する。
 *
 * 設計判断:
 *   - **HMAC-SHA1 は Web Crypto API (`crypto.subtle.sign('HMAC', ...)`) を使用**。
 *     `@noble/hashes` 導入は Vite 5 dev mode の subpath import resolve で
 *     つまずきが多く (`.js` 拡張子問題、v1/v2 dual インストール時の混線)、
 *     外部依存ゼロのほうが堅牢。bundle 増分も削減。
 *   - **HMAC-SHA1 を使うのは RFC 6238 既定アルゴリズム** + Google Authenticator
 *     / Authy / 1Password との相互運用のため。SHA1 をパスワードハッシュ用途には
 *     使わない (HMAC 構造下では collision 耐性は鍵長 ≥ 出力長で問題なし)。
 *   - Web Crypto は async API のため `computeTotp` も async に。フックは
 *     `useEffect` で tick ごとに再計算する。
 *   - Base32 (RFC 4648) decoder は ~25 行で自前実装し、`@scure/base` 等の
 *     追加依存を避ける (CLAUDE.md §6.2 Simplicity)。
 *   - period / digits はオプション化するが、既定値は Google Authenticator と
 *     同じ (period=30, digits=6)。
 *
 * セキュリティノート:
 *   - 平文 `totpSecret` (Base32 文字列) は復号後 in-memory にのみ存在し、
 *     親 (sessionStore + Vault payload) が所有。本ファイルは zeroize
 *     責務を **持たない** (TOTP secret は復元のたびに再生成される一時オブジェクト)。
 *   - 1 秒ごとに呼ばれる経路では `importKey` を毎回は呼ばず、フック側で
 *     `importHmacSha1Key` 結果を memoize して `computeTotpWithKey` を使う。
 *     `computeTotp` は CryptoKey 未キャッシュ時 (初回 / フォールバック) のみ使う。
 */

export interface TotpOptions {
  /** 周期 (秒)。RFC 6238 既定 30、Google Authenticator と一致。 */
  periodSec?: number

  /** OTP 桁数。RFC 6238 既定 6、Google Authenticator と一致。 */
  digits?: number
}

export const TOTP_DEFAULT_PERIOD_SEC = 30
export const TOTP_DEFAULT_DIGITS = 6

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

/**
 * Base32 (RFC 4648) decoder。空白 / 小文字 / `=` パディング / ハイフン等の
 * 区切り文字を許容して、`A-Z` `2-7` だけを 5 bit ずつ抽出する。
 *
 * Google Authenticator が出力する形式 (`ABCD EFGH IJKL` のような空白入り) と
 * 1Password / Authy が出力する形式 (`abcdefghijklmnop`) の両方を受け入れる。
 *
 * @throws `Error` 有効な base32 文字を 1 つも含まない場合 (= 空シークレット)
 * @throws `Error` 不正な文字を含む場合
 */
export const decodeBase32 = (input: string): Uint8Array => {
  // 区切り類は捨ててから検査する。`=` も区切り扱い (RFC 4648 padding)。
  const cleaned = input.toUpperCase().replace(/[\s=-]/g, '')
  if (cleaned.length === 0) {
    throw new Error('decodeBase32: empty input')
  }

  // 各 5-bit 値を bit 配列に詰めてから 8-bit ずつ取り出す。
  const bits: number[] = []
  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i] as string
    const v = BASE32_ALPHABET.indexOf(ch)
    if (v < 0) {
      throw new Error(`decodeBase32: invalid character "${ch}" at index ${i}`)
    }
    for (let b = 4; b >= 0; b--) bits.push((v >> b) & 1)
  }

  const byteLen = Math.floor(bits.length / 8)
  if (byteLen === 0) {
    throw new Error('decodeBase32: input too short')
  }

  const bytes = new Uint8Array(byteLen)
  for (let i = 0; i < byteLen; i++) {
    let byte = 0
    for (let j = 0; j < 8; j++) {
      const bit = bits[i * 8 + j]

      byte = (byte << 1) | (bit ?? 0)
    }
    bytes[i] = byte
  }

  return bytes
}

/**
 * 64-bit big-endian counter buffer を構築する (RFC 4226 §5.3)。
 * `BigInt` を使うことで JavaScript の number (53 bit) では表現できない
 * 大きな time 値も安全に扱う。
 */
const counterToBuffer = (counter: bigint): Uint8Array => {
  const buf = new Uint8Array(8)
  let v = counter
  for (let i = 7; i >= 0; i--) {
    buf[i] = Number(v & 0xffn)
    v >>= 8n
  }

  return buf
}

/**
 * HMAC-SHA1 用 CryptoKey を secret bytes からインポートして返す。
 * フック側で memoize し、tick ごとの importKey 呼出を避けるために公開する。
 */
export const importHmacSha1Key = (secretBytes: Uint8Array): Promise<CryptoKey> =>
  crypto.subtle.importKey(
    'raw',
    secretBytes as Uint8Array<ArrayBuffer>,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  )

/** 事前インポート済み CryptoKey で HMAC-SHA1 署名を計算する。 */
const hmacSha1WithKey = async (key: CryptoKey, message: Uint8Array): Promise<Uint8Array> => {
  const mac = await crypto.subtle.sign('HMAC', key, message as Uint8Array<ArrayBuffer>)

  return new Uint8Array(mac)
}

/**
 * Web Crypto の `subtle.sign('HMAC', ...)` で HMAC-SHA1 を計算する。
 * CryptoKey 未キャッシュ時 (初回 / フォールバック) 用。通常のフックは
 * `importHmacSha1Key` でキャッシュした key を `computeTotpWithKey` に渡す。
 */
const hmacSha1 = async (secret: Uint8Array, message: Uint8Array): Promise<Uint8Array> => {
  const key = await importHmacSha1Key(secret)

  return hmacSha1WithKey(key, message)
}

/**
 * HOTP (RFC 4226 §5.3): HMAC-SHA1 → 4-byte dynamic truncation → modulo 10^digits。
 */
const generateHotp = async (
  secret: Uint8Array,
  counter: bigint,
  digits: number,
): Promise<string> => {
  const mac = await hmacSha1(secret, counterToBuffer(counter))
  // dynamic offset = 下位 4 bit
  const offset = (mac[mac.length - 1] ?? 0) & 0x0f

  const binary =
    (((mac[offset] ?? 0) & 0x7f) << 24) |
    (((mac[offset + 1] ?? 0) & 0xff) << 16) |
    (((mac[offset + 2] ?? 0) & 0xff) << 8) |
    ((mac[offset + 3] ?? 0) & 0xff)

  const mod = 10 ** digits
  const code = binary % mod

  return code.toString().padStart(digits, '0')
}

export interface TotpResult {
  /** 現在の OTP コード (digits 桁の数字、ゼロ詰め)。 */
  readonly code: string

  /** 周期 (ミリ秒)。 */
  readonly periodMs: number

  /** 現周期の開始時刻 (UNIX epoch ms)。 */
  readonly periodStartMs: number

  /** 現周期の終了時刻 (UNIX epoch ms、次の OTP に切替わる時刻)。 */
  readonly expiresAtMs: number

  /** 周期内で経過したミリ秒。`0 <= elapsedMs < periodMs`。 */
  readonly elapsedMs: number

  /** 周期完了までの残りミリ秒。`0 < remainingMs <= periodMs`。 */
  readonly remainingMs: number
}

/**
 * `secretBytes` と `nowMs` から RFC 6238 TOTP コード + 周期情報を計算する。
 *
 * Web Crypto を使うため async。フックから 1 秒ごとに呼ぶことを想定し、
 * `secretBytes` は事前に `decodeBase32` の結果を memoize して再利用すること。
 */
export const computeTotp = async (
  secretBytes: Uint8Array,
  nowMs: number,
  options: TotpOptions = {},
): Promise<TotpResult> => {
  const periodSec = options.periodSec ?? TOTP_DEFAULT_PERIOD_SEC
  const digits = options.digits ?? TOTP_DEFAULT_DIGITS
  const periodMs = periodSec * 1000
  const counter = BigInt(Math.floor(nowMs / periodMs))
  const periodStartMs = Number(counter) * periodMs
  const elapsedMs = nowMs - periodStartMs
  const remainingMs = periodMs - elapsedMs
  const code = await generateHotp(secretBytes, counter, digits)

  return {
    code,
    periodMs,
    periodStartMs,
    expiresAtMs: periodStartMs + periodMs,
    elapsedMs,
    remainingMs,
  }
}

/**
 * `computeTotp` の base32 入力版。フック以外の同期 1 回計算用 (例: list 画面の
 * バッジ表示)。フックは `decodeBase32` 結果を memoize するため本関数は使わない。
 */
export const computeTotpForBase32 = (
  base32Secret: string,
  nowMs: number,
  options: TotpOptions = {},
): Promise<TotpResult> => computeTotp(decodeBase32(base32Secret), nowMs, options)

/** 事前インポート済み CryptoKey を使った HOTP 計算 (importKey 呼出なし)。 */
const generateHotpWithKey = async (
  key: CryptoKey,
  counter: bigint,
  digits: number,
): Promise<string> => {
  const mac = await hmacSha1WithKey(key, counterToBuffer(counter))
  const offset = (mac[mac.length - 1] ?? 0) & 0x0f

  const binary =
    (((mac[offset] ?? 0) & 0x7f) << 24) |
    (((mac[offset + 1] ?? 0) & 0xff) << 16) |
    (((mac[offset + 2] ?? 0) & 0xff) << 8) |
    ((mac[offset + 3] ?? 0) & 0xff)

  const mod = 10 ** digits
  const code = binary % mod

  return code.toString().padStart(digits, '0')
}

/**
 * 事前インポート済み CryptoKey から TOTP を計算する。
 * フック (`useTotp`) は `importHmacSha1Key` でキャッシュした key を本関数に渡すことで
 * 1 秒ごとの `importKey` 呼出を回避する。
 */
export const computeTotpWithKey = async (
  key: CryptoKey,
  nowMs: number,
  options: TotpOptions = {},
): Promise<TotpResult> => {
  const periodSec = options.periodSec ?? TOTP_DEFAULT_PERIOD_SEC
  const digits = options.digits ?? TOTP_DEFAULT_DIGITS
  const periodMs = periodSec * 1000
  const counter = BigInt(Math.floor(nowMs / periodMs))
  const periodStartMs = Number(counter) * periodMs
  const elapsedMs = nowMs - periodStartMs
  const remainingMs = periodMs - elapsedMs
  const code = await generateHotpWithKey(key, counter, digits)

  return {
    code,
    periodMs,
    periodStartMs,
    expiresAtMs: periodStartMs + periodMs,
    elapsedMs,
    remainingMs,
  }
}
